import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import panelApi from "@/lib/panelApi";
import { useFetch } from "@/hooks/useFetch";
import { usePayrollSettings } from "@/context/PayrollSettingsContext";
import { useMinsFor } from "@/lib/useBaseMins";
import { computeAutoBonus } from "@/lib/bonusConfig";
import { lineBreakdown } from "@/lib/payrollCompute";
import { usePayrollResolver } from "@/lib/usePayrollResolver";
import { useCompOverrides, ovKey } from "@/lib/compOverrides";

const BEN_CATS = [["fuel", "Combustível"], ["meal", "Vale refeição"], ["commute", "Deslocamento"]];
const BEN_SOURCES = new Set(["fuel", "meal", "commute", "bonus_auto"]);
const BEN_KEYS = ["fuel", "meal", "commute"];
const FREQ_KEY = { fuel: "fuel_price", meal: "meal_voucher_value" };

const payQLabel = (tags) => {
  const t = [...tags];
  if (t.length > 1) return "1ª e 2ª quinzena (varia por colaborador)";
  return t[0] === 1 ? "Paga na 1ª quinzena" : t[0] === 2 ? "Paga na 2ª quinzena" : "Paga em cada quinzena";
};

// Despesas calculadas pelo sistema no período (Mensal = 1ª + 2ª quinzena), sem contar nada duas vezes:
// folha (mesma pipeline do Custo Operacional, sem os benefícios apurados/produtividade) + benefícios apurados + produtividade.
export function useAutoExpenses(year, month, period, forBaseId) {
  const mStr = `${year}-${String(month).padStart(2, "0")}`;
  const bp = forBaseId ? { base_id: forBaseId } : {};
  const p1 = useFetch("/admin/payroll", { month: mStr, quinzena: 1, ...bp });
  const p2 = useFetch("/admin/payroll", { month: mStr, quinzena: 2, ...bp });
  const r1 = useFetch("/admin/reports", { year, month, quinzena: 1, ...bp });
  const r2 = useFetch("/admin/reports", { year, month, quinzena: 2, ...bp });
  const ben = useFetch("/admin/benefits-report", { year, month, ...bp });
  const cfg = useFetch("/config");
  const tiersData = useFetch("/admin/bonus-tiers");
  const { settings } = usePayrollSettings();
  const minsFor = useMinsFor();
  const R = usePayrollResolver(mStr, forBaseId);
  const freqQ = useQuery({ queryKey: ["panel-benefit-frequencies"], queryFn: async () => (await panelApi.get("/panel/benefit-frequencies")).data?.frequencies || {} });
  const ovQ = useCompOverrides(mStr);

  const items = useMemo(() => {
    const qs = period === "mensal" ? [1, 2] : [Number(period)];
    const mins = { ...(cfg.data || {}), mestre_min: cfg.data?.mestre_min ?? settings.mestre_min };
    const tiers = tiersData.data?.tiers || [];
    const freqAll = freqQ.data || {};
    // Quinzena em que cada benefício é pago: componente da folha (1ª/2ª) > frequência da base (q1/q2) > ambas.
    const compQ = {};
    [p1, p2].forEach((p) => (R.prepare(p.data)?.payroll?.lines || []).forEach((l) => (l.items || []).forEach((it) => {
      const q = Number(R.flags.quinzenaItems[it.id]);
      if (BEN_KEYS.includes(it.source) && q && !R.flags.disabled[it.id]) (compQ[l.user_id] = compQ[l.user_id] || {})[it.source] = q;
    })));
    const freqKey = (uid, k) => {
      if (k !== "commute") return FREQ_KEY[k];
      const c = R.collabOf(uid);
      return c?.role === "funcionario" ? "commute_administrativo" : c?.vehicle_type === "motociclista" ? "commute_motociclista" : "commute_ciclista";
    };
    const payQOf = (uid, k) => {
      if (compQ[uid]?.[k]) return compQ[uid][k];
      const b = R.baseOf(uid);
      const f = (b && freqAll[b]?.[freqKey(uid, k)]) || freqAll.global?.[freqKey(uid, k)];
      return f === "q1" ? 1 : f === "q2" ? 2 : null;
    };
    const acc = {};
    const ovs = ovQ.data || {};
    const ovBen = {}; // quinzena -> colaborador -> benefício editado manualmente na Folha
    const sched = {};
    const tag = (key, label, t) => { (sched[key] = sched[key] || { label, tags: new Set() }).tags.add(t); };
    const add = (key, description, category, type, value) => {
      if (!value) return;
      const e = acc[key] || (acc[key] = { id: `auto-${key}`, auto: true, description, category, type, value: 0 });
      e.value += value;
    };
    qs.forEach((q) => {
      const pay = R.prepare((q === 1 ? p1 : p2).data)?.payroll;
      const dlv = {};
      ((q === 1 ? r1 : r2).data?.rows || []).forEach((r) => { dlv[r.id] = r.deliveries || 0; });
      (pay?.lines || []).forEach((l) => {
        // Benefícios apurados e produtividade entram em linhas próprias abaixo.
        const rl0 = R.resolveSalary(l);
        const ovOf = (it) => ovs[ovKey(q, l.user_id, it.id)];
        const rl = { ...rl0, items: (rl0.items || []).map((it) => {
          const v = ovOf(it);
          if (v == null) return it;
          if (["fuel", "meal", "commute"].includes(it.source)) ((ovBen[q] = ovBen[q] || {})[l.user_id] = ovBen[q][l.user_id] || {})[it.source] = Number(v);
          return { ...it, value: Number(v) };
        }) };
        // Produtividade só quando a folha paga: componente automático ativo nesta quinzena (mesma regra do lineBreakdown).
        const autoItems = (rl.items || []).filter((it) => (R.flags.auto[it.id] || it.source === "bonus_auto") && !R.flags.disabled[it.id]
          && (!R.flags.quinzenaItems[it.id] || Number(R.flags.quinzenaItems[it.id]) === q));
        if (autoItems.length) {
          const bn = computeAutoBonus(dlv[l.user_id] || 0, minsFor(l.user_id, mins), tiers, R.contractOf(l.user_id));
          const b = R.baseOf(l.user_id);
          const pk = bn.level ? `prod_${String(bn.level).normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()}` : null;
          const f = pk && ((b && freqAll[b]?.[pk]) || freqAll.global?.[pk]);
          if (bn.value) tag("prod", "Produtividade", f === "q1" ? 1 : f === "q2" ? 2 : "ambas");
          if (!f || f === "quinzena" || f === `q${q}`) add("prod", "Bônus de produtividade pago na folha", "Produtividade", "variavel", autoItems.reduce((a, it) => a + (ovOf(it) != null ? Number(ovOf(it)) : bn.value), 0));
        }
        const pjPart = (rl.items || []).filter((it) => it.source === "pj_deliveries" && !R.flags.disabled[it.id]).reduce((a, it) => a + (Number(it.value) || 0), 0);
        const line = { ...rl, items: (rl.items || []).filter((it) => it.source !== "pj_deliveries" && !BEN_SOURCES.has(it.source) && !R.flags.auto[it.id]) };
        const b = lineBreakdown(line, { ...R.flags, individual: R.individualFor(l.user_id, q), period: q });
        const adm = l.role === "funcionario";
        const isPj = pjPart > 0 || /pj/i.test(String(l.contract_type || ""));
        if (isPj) add("pj", "Pagamento PJ (entregas + proventos − descontos)", "Folha PJ", "variavel", pjPart + b.net);
        else if (adm) add("clt-adm", "Salários do administrativo (proventos − descontos)", "Folha Administrativo", "fixa", b.net);
        else add("clt-ent", "Salários CLT dos entregadores (proventos − descontos)", "Folha CLT", "fixa", b.net);
      });
      (ben.data?.drivers || []).filter((d) => !R.outOfBase(d.id)).forEach((d) => (d.days || []).forEach((dy) => {
        const day = Number(String(dy.date).slice(8, 10));
        const dq = day <= 15 ? 1 : 2;
        BEN_CATS.forEach(([k, label]) => {
          // Pago só numa quinzena: o mês inteiro entra nela; sem marcação, cada dia entra na sua quinzena.
          const pq = payQOf(d.id, k);
          if (Number(dy[k])) tag(`ben-${k}`, label, pq || "ambas");
          if ((pq || dq) !== q) return;
          if (ovBen[q]?.[d.id]?.[k] != null) return;
          add(`ben-${k}`, `${label} apurado no período`, label, "variavel", Number(dy[k]) || 0);
        });
      }));
      Object.values(ovBen[q] || {}).forEach((m) => BEN_CATS.forEach(([k, label]) => { if (m[k] != null) add(`ben-${k}`, `${label} apurado no período`, label, "variavel", m[k]); }));
    });
    const items = Object.values(acc).filter((e) => Math.abs(e.value) > 0.004)
      .map((e) => (sched[e.id.slice(5)] ? { ...e, payQ: payQLabel(sched[e.id.slice(5)].tags) } : e));
    const shown = new Set(items.map((e) => e.id.slice(5)));
    const elsewhere = Object.entries(sched).filter(([k]) => !shown.has(k)).map(([, s]) => ({ label: s.label, payQ: payQLabel(s.tags) }));
    return { items, elsewhere };
  }, [period, p1.data, p2.data, r1.data, r2.data, ben.data, cfg.data, tiersData.data, settings, minsFor, R, freqQ.data, ovQ.data]);

  const loading = [p1, p2, r1, r2, ben].some((x) => x.loading && !x.data) || !R.ready || freqQ.isLoading || ovQ.isLoading;
  return { items: items.items, elsewhere: items.elsewhere, loading };
}
